import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  obtenerCasosCliente, 
  obtenerVerificacionCliente, 
  obtenerDocumentosCliente 
} from '../../services/clienteAuthService';
import './MiEspacio.css';

const MiEspacio = () => {
  const [cliente, setCliente] = useState(null);
  const [casos, setCasos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCaso, setSelectedCaso] = useState(null);
  const [verificacion, setVerificacion] = useState(null);
  const [documentos, setDocumentos] = useState([]);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const cargarDetalleCaso = useCallback(async (casoId) => {
    try {
      const verifData = await obtenerVerificacionCliente(casoId);
      setVerificacion(verifData);
      
      const docsData = await obtenerDocumentosCliente(casoId);
      setDocumentos(docsData);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
    }
  }, []);

  const cargarCasos = useCallback(async (clienteId) => {
    try {
      setLoading(true);
      setError('');
      const casosData = await obtenerCasosCliente(clienteId);
      setCasos(casosData);
      
      if (casosData.length > 0) {
        setSelectedCaso(casosData[0]);
        await cargarDetalleCaso(casosData[0].id);
      } else {
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error al cargar casos:', error);
      setError('Error al cargar casos: ' + error.message);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  }, [cargarDetalleCaso]);

  useEffect(() => {
    const sessionData = sessionStorage.getItem('usuario');
    if (!sessionData) {
      navigate('/login');
      return;
    }
    
    const usuario = JSON.parse(sessionData);
    if (usuario.type !== 'cliente') {
      navigate('/login');
      return;
    }
    
    const clienteData = usuario.cliente;
    if (!clienteData) {
      navigate('/login');
      return;
    }
    
    setCliente(clienteData);
    cargarCasos(clienteData.id);
  }, [navigate, cargarCasos]);

  const handleCasoChange = async (caso) => {
    setSelectedCaso(caso);
    await cargarDetalleCaso(caso.id);
  };

  const calcularProgreso = () => {
    if (!verificacion) return 0;
    const items = ['runt', 'simit', 'embargos', 'improntas', 'soat', 'tecnomecanica'];
    const completados = items.filter(item => verificacion[item]?.verificada).length;
    return Math.round((completados / items.length) * 100);
  };

  const getEstadoTexto = (estado) => {
    const textos = {
      nuevo: 'Nuevo',
      en_verificacion: 'En Verificacion',
      en_proceso: 'En Proceso',
      completado: 'Completado',
      cancelado: 'Cancelado'
    };
    return textos[estado] || estado;
  };

  const getEstadoClase = (estado) => {
    const clases = {
      nuevo: 'estado-nuevo',
      en_verificacion: 'estado-verificacion',
      en_proceso: 'estado-proceso',
      completado: 'estado-completado',
      cancelado: 'estado-cancelado'
    };
    return clases[estado] || '';
  };

  const verDocumento = (doc) => {
    if (doc.contenido) {
      const ventana = window.open();
      ventana.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${doc.nombreOriginal}</title>
            <meta charset="UTF-8">
            <style>
              body { margin: 0; padding: 0; font-family: sans-serif; }
              iframe { width: 100%; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${doc.contenido}"></iframe>
          </body>
        </html>
      `);
    } else {
      alert('No se puede visualizar este documento');
    }
  };

  if (loading) return <div className="loading">Cargando su informacion...</div>;

  const progreso = calcularProgreso();

  return (
    <div className="mi-espacio-container">
      <div className="mi-espacio">
        <div className="saludo">
          <h1>Hola, {cliente?.nombre}</h1>
          <p>Seleccione su caso para ver el detalle</p>
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {casos.length === 0 ? (
          <div className="sin-casos">
            <p>No tiene casos activos actualmente.</p>
            <p>Si contrato un servicio con Golden Cars Legal, pronto se asignara su caso.</p>
            <p className="info-adicional">Contacte a su asesor para mas informacion.</p>
          </div>
        ) : (
          <>
            <div className="casos-selector">
              <label>Seleccione su caso:</label>
              <select 
                value={selectedCaso?.id || ''} 
                onChange={(e) => {
                  const caso = casos.find(c => c.id === e.target.value);
                  if (caso) handleCasoChange(caso);
                }}
              >
                {casos.map(caso => (
                  <option key={caso.id} value={caso.id}>
                    {caso.vehiculo?.marca} {caso.vehiculo?.modelo} - {caso.vehiculo?.placa}
                  </option>
                ))}
              </select>
            </div>

            {selectedCaso && (
              <>
                <div className="caso-tarjeta">
                  <div className="tarjeta-header">
                    <h3>Informacion del Vehiculo</h3>
                  </div>
                  <div className="tarjeta-body">
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Marca</span>
                        <span className="info-valor">{selectedCaso.vehiculo?.marca || 'No especificada'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Modelo</span>
                        <span className="info-valor">{selectedCaso.vehiculo?.modelo || 'No especificado'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Placa</span>
                        <span className="info-valor">{selectedCaso.vehiculo?.placa?.toUpperCase() || 'No especificada'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Anio</span>
                        <span className="info-valor">{selectedCaso.vehiculo?.año || 'No especificado'}</span>
                      </div>
                      {selectedCaso.vehiculo?.precio && (
                        <div className="info-item">
                          <span className="info-label">Precio</span>
                          <span className="info-valor">${Number(selectedCaso.vehiculo.precio).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="caso-tarjeta">
                  <div className="tarjeta-header">
                    <h3>Estado del Servicio</h3>
                  </div>
                  <div className="tarjeta-body">
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Tipo de Servicio</span>
                        <span className="info-valor">
                          {selectedCaso.tipoServicio === 'compra' ? 'Asesoria en Compra' :
                           selectedCaso.tipoServicio === 'venta' ? 'Asesoria en Venta' :
                           selectedCaso.tipoServicio === 'verificacion' ? 'Verificacion Legal' : 'Tramite de Traspaso'}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Estado</span>
                        <span className={`estado-badge ${getEstadoClase(selectedCaso.estado)}`}>
                          {getEstadoTexto(selectedCaso.estado)}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Fecha de Inicio</span>
                        <span className="info-valor">{selectedCaso.fechaCreacion?.toLocaleDateString()}</span>
                      </div>
                      {selectedCaso.honorarios && (
                        <div className="info-item">
                          <span className="info-label">Honorarios</span>
                          <span className="info-valor">${Number(selectedCaso.honorarios).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="caso-tarjeta progreso-tarjeta">
                  <div className="tarjeta-header">
                    <h3>Progreso de Verificacion Legal</h3>
                  </div>
                  <div className="tarjeta-body">
                    <div className="progress-bar-container">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progreso}%` }}></div>
                      </div>
                      <span className="progress-text">{progreso}% completado</span>
                    </div>
                  </div>
                </div>

                <div className="caso-tarjeta">
                  <div className="tarjeta-header">
                    <h3>Checklist de Verificaciones</h3>
                  </div>
                  <div className="tarjeta-body">
                    <div className="checklist-grid">
                      <div className={`checklist-item ${verificacion?.runt?.verificada ? 'completado' : 'pendiente'}`}>
                        <span className="checklist-icon">{verificacion?.runt?.verificada ? '✓' : '○'}</span>
                        <span>Verificacion RUNT</span>
                      </div>
                      <div className={`checklist-item ${verificacion?.simit?.verificada ? 'completado' : 'pendiente'}`}>
                        <span className="checklist-icon">{verificacion?.simit?.verificada ? '✓' : '○'}</span>
                        <span>Consulta SIMIT (Multas)</span>
                      </div>
                      <div className={`checklist-item ${verificacion?.embargos?.verificada ? 'completado' : 'pendiente'}`}>
                        <span className="checklist-icon">{verificacion?.embargos?.verificada ? '✓' : '○'}</span>
                        <span>Consulta de Embargos</span>
                      </div>
                      <div className={`checklist-item ${verificacion?.improntas?.verificada ? 'completado' : 'pendiente'}`}>
                        <span className="checklist-icon">{verificacion?.improntas?.verificada ? '✓' : '○'}</span>
                        <span>Validacion de Improntas</span>
                      </div>
                      <div className={`checklist-item ${verificacion?.soat?.verificada ? 'completado' : 'pendiente'}`}>
                        <span className="checklist-icon">{verificacion?.soat?.verificada ? '✓' : '○'}</span>
                        <span>Verificacion SOAT</span>
                      </div>
                      <div className={`checklist-item ${verificacion?.tecnomecanica?.verificada ? 'completado' : 'pendiente'}`}>
                        <span className="checklist-icon">{verificacion?.tecnomecanica?.verificada ? '✓' : '○'}</span>
                        <span>Verificacion TecnoMecanica</span>
                      </div>
                    </div>
                  </div>
                </div>

                {documentos.length > 0 && (
                  <div className="caso-tarjeta">
                    <div className="tarjeta-header">
                      <h3>Documentos Compartidos</h3>
                    </div>
                    <div className="tarjeta-body">
                      <div className="documentos-grid">
                        {documentos.map(doc => (
                          <div key={doc.id} className="documento-item">
                            <div className="documento-icono">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div className="documento-info">
                              <p className="documento-nombre">{doc.nombreOriginal}</p>
                              <p className="documento-fecha">{doc.fechaSubida?.toLocaleDateString()}</p>
                            </div>
                            <button onClick={() => verDocumento(doc)} className="btn-ver-documento">
                              Ver
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Modal informativo integrado */}
      {showModal && (
        <div className="modal-overlay-info" onClick={() => setShowModal(false)}>
          <div className="modal-content-info" onClick={e => e.stopPropagation()}>
            <div className="modal-header-info">
              <h3>Sin casos disponibles</h3>
              <button className="modal-close-info" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body-info">
              <div className="modal-icon-info">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8v4M12 16h.01" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p>Actualmente no tiene casos asignados.</p>
              <p>Si ha contratado un servicio con Golden Cars Legal, en breve su asesor asignara su caso.</p>
              <p>Si tiene alguna duda, contacte a su asesor para mas informacion.</p>
            </div>
            <div className="modal-footer-info">
              <button className="modal-btn-info" onClick={() => setShowModal(false)}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiEspacio;